import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiClientError, type ApiClientErrorCode, type PartnerListItem } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { PARTNER_CATEGORIES, RASPIL_CATEGORY } from './categories';
import { DEFAULT_PARTNER_CITY } from './cities';
import { safeReturnTo } from './returnTo';
import { PartnersListView, type PartnersListState } from './PartnersListView';

/**
 * Container Экрана 2.8.2 «Список партнёров категории» (Блок 8, ADR-0080, Фаза D2).
 *
 * Живёт на `/partners/list?category=<канон>` (R3: persistent intent, bookmark-able;
 * проставлена экраном категорий 2.8.1 ИЛИ резолвом slug из калькулятора, Фаза F2).
 * Поддерживает repeated-param `?category=A&category=B` — merged-режим (`facades-radius`
 * → «Фасады плёнка» + «Фасады краска»): N запросов + дедуп по id, контракт не меняется.
 * Ни одной валидной категории → редирект на `/partners` (экран категорий). `returnTo`
 * (валидный внутренний путь) → «Назад» ведёт в калькулятор-анкету (R3, F2).
 *
 * Город — локальный стейт селекта, дефолт «Астана» (профиль-дефолт позже).
 * Подкатегория-подтумблер — ТОЛЬКО для «Распил…». Подкатегория ОБЯЗАТЕЛЬНА: пока не
 * выбрана (`needsSubcategory`), запрос не идёт, Presenter показывает подсказку
 * «Выберите подкатегорию». Запрос — ТОЛЬКО через типизированный
 * `apiClient.partners.list` (P-012, без прямого fetch). Смена города/подкатегории →
 * перезапрос.
 *
 * 5 состояний (P-021): запрос идёт сразу (category+city всегда заданы), поэтому
 * Container эмитит loading → success/empty/error; idle поддержан Presenter'ом
 * (нейтральный плейсхолдер), но при наличии дефолтов не достигается. empty
 * (нет партнёров по фильтру) = «Скоро появятся». Тап по карточке → детальная
 * (`/partners/:id`, Фаза D3; сейчас placeholder). Back = история (BottomBar, P-036).
 */
function toApiErr(err: unknown): { code: ApiClientErrorCode; message: string } {
  // Не leak-аем String(err) (security, как FurnitureSearch): non-ApiClientError →
  // пустой message.
  return err instanceof ApiClientError
    ? { code: err.code, message: err.message }
    : { code: 'INTERNAL_ERROR', message: '' };
}

const KNOWN_CATEGORIES: readonly string[] = PARTNER_CATEGORIES;

/** Высокий limit для merged-режима: данные малы (§3 HANDOVER), без курсора. = max схемы. */
const MERGED_LIMIT = 100;

export function PartnersList() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // F2: одна ИЛИ несколько категорий (repeated-param `?category=A&category=B` для
  // `facades-radius` → «Фасады плёнка» + «Фасады краска»). Валидация каждой по
  // справочнику (verbatim, P-001 — без normalize); неизвестные отбрасываются.
  const rawCategories = params.getAll('category');
  const categories = rawCategories.filter((c) => KNOWN_CATEGORIES.includes(c));
  // Стабильный ключ для зависимостей эффекта (массив пересоздаётся каждый рендер).
  const categoryKey = categories.join('|');
  const hasCategories = categories.length > 0;
  const isMerged = categories.length > 1;

  // Подтумблер «Распил…» — только в одиночном режиме именно этой категории.
  const showSubcategories = categories.length === 1 && categories[0] === RASPIL_CATEGORY;

  // returnTo (валидный внутренний путь) → «Назад» ведёт в калькулятор-анкету (R3).
  const returnTo = safeReturnTo(params.get('returnTo'));

  const [city, setCity] = useState<string>(DEFAULT_PARTNER_CITY);
  const [subcategory, setSubcategory] = useState<string | undefined>(undefined);
  // Подкатегория применима лишь к «Распил…»; иначе всегда «все».
  const effectiveSubcategory = showSubcategories ? subcategory : undefined;

  // Гейт «Распил…»: подкатегория — ОБЯЗАТЕЛЬНЫЙ первый шаг. Пока она не выбрана,
  // запрос НЕ выполняется (показываем подсказку «Выберите подкатегорию»). Для прочих
  // 10 категорий и merged-режима подкатегорий нет → гейт всегда false (список сразу).
  const needsSubcategory = showSubcategories && subcategory === undefined;

  const [items, setItems] = useState<readonly PartnerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);

  useEffect(() => {
    if (!hasCategories) return; // нет валидных категорий → редирект ниже, без запроса
    if (needsSubcategory) {
      // Гейт «Распил…»: подкатегория не выбрана → запрос НЕ идёт. Сбрасываем в нейтраль
      // (не loading), чтобы Presenter показал подсказку «Выберите подкатегорию».
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    // Сброс stale-результатов при смене фильтра (как FurnitureSearch): прежние items
    // относятся к предыдущему запросу и невалидны для нового — иначе мелькание.
    setItems([]);
    setLoading(true);
    setError(null);

    // Один запрос на категорию; merged-режим (≥2) шлёт высокий limit без курсора и
    // объединяет результаты с дедупом по id (контракт `partners` не меняется — repeated-
    // param живёт только во фронт-URL). Любой упавший запрос → состояние error целиком.
    Promise.all(
      categories.map((category) =>
        apiClient.partners.list({
          category,
          city,
          subcategory: effectiveSubcategory,
          ...(isMerged ? { limit: MERGED_LIMIT } : {}),
        }),
      ),
    ).then(
      (responses) => {
        if (cancelled) return;
        const byId = new Map<string, PartnerListItem>();
        for (const res of responses) {
          for (const it of res.items) {
            if (!byId.has(it.id)) byId.set(it.id, it);
          }
        }
        setItems([...byId.values()]);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(toApiErr(err));
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // categoryKey/isMerged выводятся из categories — перечисляем стабильные значения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, city, effectiveSubcategory, hasCategories, isMerged, needsSubcategory]);

  const state = useMemo<PartnersListState>(() => {
    if (error) return 'error';
    if (loading) return 'loading';
    return items.length === 0 ? 'empty' : 'success';
  }, [error, loading, items.length]);

  if (!hasCategories) return <Navigate to="/partners" replace />;

  // Заголовок: одиночная категория — её имя; merged — объединение через « / »
  // (derived из URL, без доп. параметров; UX-источник истины — единый список).
  const heading = categories.join(' / ');

  return (
    <PartnersListView
      category={heading}
      state={state}
      city={city}
      onCityChange={setCity}
      showSubcategories={showSubcategories}
      needsSubcategory={needsSubcategory}
      subcategory={effectiveSubcategory}
      onSubcategoryChange={setSubcategory}
      items={items}
      error={error ?? undefined}
      onItemClick={(id) => navigate(`/partners/${encodeURIComponent(id)}`)}
      backTo={returnTo ?? undefined}
    />
  );
}
